import React, { useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { HelpTip } from "@/components/HelpTip";
import { WalletFundingHistory } from "@/components/WalletFundingHistory";
import { WalletBankConfig } from "@/components/WalletBankConfig";
import { AdminWalletLedger } from "@/components/AdminWalletLedger";
import { WalletQuoteSimulator } from "@/components/WalletQuoteSimulator";
import { WalletCommissionPolicyPanel } from "@/components/WalletCommissionPolicyPanel";
import { WalletCommissionEarnings } from "@/components/WalletCommissionDetails";
import { TopupRequestDialog } from "@/components/TopupRequestDialog";
import { LowPlafondPanel } from "@/components/LowPlafondPanel";
import { ConfirmButton } from "@/components/ConfirmButton";
import { Wallet, Plus, ArrowUpRight, ArrowDownLeft } from "lucide-react";

const eur = (n) => euro(n);

export default function AdminWallet() {
  const [mov, setMov] = useState({ balance: 0, total_out: 0, total_in: 0, count: 0, items: [] });
  const loadMov = () => api.get("/admin/wallet/movements").then((r) => setMov(r.data)).catch(() => {});
  const [rc, setRc] = useState(null);
  const [rcBusy, setRcBusy] = useState(false);
  const loadRc = () => api.get("/admin/recharge-config").then((r) => setRc(r.data)).catch(() => {});
  useEffect(() => { loadMov(); loadRc(); }, []);

  const setPreset = (i, v) => setRc((x) => ({ ...x, presets: x.presets.map((p, idx) => (idx === i ? v : p)) }));
  const addPreset = () => setRc((x) => ({ ...x, presets: [...x.presets, 0] }));
  const rmPreset = (i) => setRc((x) => ({ ...x, presets: x.presets.filter((_, idx) => idx !== i) }));
  const setTier = (i, k, v) => setRc((x) => ({ ...x, tiers: x.tiers.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)) }));
  const addTier = () => setRc((x) => ({ ...x, tiers: [...x.tiers, { min: 0, dealer: 0, master: 0 }] }));
  const rmTier = (i) => setRc((x) => ({ ...x, tiers: x.tiers.filter((_, idx) => idx !== i) }));
  const saveRc = async () => {
    setRcBusy(true);
    try {
      await api.put("/admin/recharge-config", {
        presets: rc.presets.map((p) => parseFloat(p) || 0).filter((p) => p > 0),
        tiers: rc.tiers.map((t) => ({ min: parseFloat(t.min) || 0, dealer: parseFloat(t.dealer) || 0, master: parseFloat(t.master) || 0 })),
        master_default_dealer_pct: parseFloat(rc.master_default_dealer_pct) || 0,
      });
      toast.success("Preset e sconti ricarica salvati");
      loadRc();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setRcBusy(false); }
  };

  const [scOpen, setScOpen] = useState(false);
  const [fundVersion, setFundVersion] = useState(0);
  const [scAmount, setScAmount] = useState("5000");
  const [scBusy, setScBusy] = useState(false);
  const selfCredit = async () => {
    const amt = parseFloat(scAmount);
    if (!amt || amt <= 0) { toast.error("Importo non valido"); return; }
    setScBusy(true);
    try {
      const { data } = await api.post("/admin/wallet/self-credit", { amount: amt });
      toast.success(`Credito admin aggiornato: ${eur(data.balance)}`);
      setScOpen(false); loadMov();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setScBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-wallet-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><Wallet className="w-4 h-4 text-primary" /><span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Amministrazione</span></div>
          <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Wallet Admin</h1>
          <p className="text-sm text-muted-foreground mt-1">Il credito con cui ricarichi dealer, master e utenti.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">
          <HelpTip cat="wallet" />
          <TopupRequestDialog onSent={() => setFundVersion((v) => v + 1)} testId="admin-topup-request" />
          <Button className="gap-2 max-w-full h-auto min-h-10 whitespace-normal" onClick={() => { setScAmount("5000"); setScOpen(true); }} data-testid="admin-self-credit-open">
            <Plus className="w-4 h-4" /> Aggiungi credito
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-6 min-w-0 glow-border sm:col-span-1" data-testid="admin-wallet-balance-card">
          <div className="w-11 h-11 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><Wallet className="w-5 h-5" /></div>
          <p className="text-xs text-muted-foreground mt-4">Saldo attuale</p>
          <p className="font-head text-4xl font-bold mt-1" data-testid="admin-wallet-balance">{eur(mov.balance)}</p>
        </Card>
        <Card className="p-6 min-w-0 flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-red-500/15 text-red-400 flex items-center justify-center shrink-0"><ArrowUpRight className="w-5 h-5" /></div>
          <div className="min-w-0 break-words"><p className="text-xs text-muted-foreground">Totale ricaricato (uscite)</p><p className="font-head text-2xl font-bold text-red-400">{eur(mov.total_out)}</p></div>
        </Card>
        <Card className="p-6 min-w-0 flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-teal-600/15 text-teal-500 flex items-center justify-center shrink-0"><ArrowDownLeft className="w-5 h-5" /></div>
          <div className="min-w-0 break-words"><p className="text-xs text-muted-foreground">Totale rientrato (storni)</p><p className="font-head text-2xl font-bold text-teal-500">{eur(mov.total_in)}</p></div>
        </Card>
      </div>

      <Card className="p-5 glow-border space-y-3" data-testid="admin-wallet-movements">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <h2 className="font-head text-lg font-semibold">Movimenti</h2>
          <span className="text-sm text-muted-foreground">{mov.count} operazioni</span>
        </div>
        {mov.items.length > 0 ? (
          <div className="divide-y divide-border rounded-lg border border-border" data-testid="admin-wallet-movements-list">
            {mov.items.map((g, i) => {
              const out = Number(g.amount) < 0;
              return (
                <div key={g.ref ? `${g.ref}-${i}` : i} className="flex items-center justify-between gap-3 px-4 py-3 text-sm" data-testid={`wallet-mov-row-${i}`}>
                  <div className="min-w-0 flex items-center gap-2">
                    {out ? <ArrowUpRight className="w-4 h-4 text-red-400 shrink-0" /> : <ArrowDownLeft className="w-4 h-4 text-teal-500 shrink-0" />}
                    <div className="min-w-0">
                      <p className="truncate">{g.description || (out ? "Ricarica" : "Storno")}</p>
                      <p className="text-xs text-muted-foreground">{g.created_at ? new Date(g.created_at).toLocaleString("it-IT") : ""}</p>
                    </div>
                  </div>
                  <span className={`font-mono font-semibold shrink-0 ${out ? "text-red-400" : "text-teal-500"}`}>{out ? "−" : "+"} {eur(Math.abs(Number(g.amount)))}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Nessun movimento registrato.</p>
        )}
      </Card>

      <WalletCommissionPolicyPanel anchorVersion={mov.items.length} />
      <WalletQuoteSimulator />
      <LowPlafondPanel onSent={() => setFundVersion((v) => v + 1)} />
      <WalletFundingHistory admin version={fundVersion} />
      <WalletCommissionEarnings />
      <WalletBankConfig />
      <AdminWalletLedger />

      {rc && (
        <Card className="p-5 glow-border space-y-5" data-testid="recharge-config-card">
          <div>
            <h2 className="font-head text-lg font-semibold">Ricariche dealer · Preset & sconti volume</h2>
            <p className="text-sm text-muted-foreground">Importi cliccabili e bonus credito in base a quanto ricarica il dealer.</p>
          </div>
          <div>
            <Label>Importi preset (€)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {rc.presets.map((p, i) => (
                <div key={i} className="flex items-center gap-1">
                  <Input type="number" min="1" className="w-24" value={p} onChange={(e) => setPreset(i, e.target.value)} data-testid={`rc-preset-${i}`} />
                  <button onClick={() => rmPreset(i)} className="text-muted-foreground hover:text-red-400" data-testid={`rc-preset-rm-${i}`}>✕</button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addPreset} data-testid="rc-preset-add">+ Importo</Button>
            </div>
          </div>
          <div>
            <Label>Sconti volume (bonus credito) — % dealer e % master</Label>
            <div className="space-y-2 mt-2">
              {rc.tiers.map((t, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`rc-tier-${i}`}>
                  <span className="text-muted-foreground">da €</span>
                  <Input type="number" min="0" className="w-28 max-w-full" value={t.min} onChange={(e) => setTier(i, "min", e.target.value)} data-testid={`rc-tier-min-${i}`} />
                  <span className="text-muted-foreground">dealer</span>
                  <Input type="number" min="0" step="0.5" className="w-16" value={t.dealer} onChange={(e) => setTier(i, "dealer", e.target.value)} data-testid={`rc-tier-dealer-${i}`} />
                  <span className="text-muted-foreground">% · master</span>
                  <Input type="number" min="0" step="0.5" className="w-16" value={t.master} onChange={(e) => setTier(i, "master", e.target.value)} data-testid={`rc-tier-master-${i}`} />
                  <span className="text-muted-foreground">%</span>
                  <button onClick={() => rmTier(i)} className="text-muted-foreground hover:text-red-400 ml-1" data-testid={`rc-tier-rm-${i}`}>✕</button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addTier} data-testid="rc-tier-add">+ Fascia</Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Label className="mb-0">% ricarica di default che il Master gira al dealer</Label>
            <Input type="number" min="0" step="0.5" className="w-20" value={rc.master_default_dealer_pct} onChange={(e) => setRc((x) => ({ ...x, master_default_dealer_pct: e.target.value }))} data-testid="rc-master-default" />
            <span className="text-muted-foreground text-sm">%</span>
          </div>
          <Button onClick={saveRc} disabled={rcBusy} className="h-auto min-h-10 whitespace-normal" data-testid="rc-save">{rcBusy ? "Salvataggio…" : "Salva preset & sconti"}</Button>
        </Card>
      )}

      <Dialog open={scOpen} onOpenChange={setScOpen}>
        <DialogContent data-testid="admin-self-credit-dialog">
          <DialogHeader><DialogTitle>Aggiungi credito al wallet admin</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Aggiunge credito (fittizio) al tuo wallet admin. Da qui poi puoi ricaricare dealer, master e utenti.</p>
            <div><Label>Importo (€)</Label><Input type="number" min="0" step="0.01" className="mt-1" value={scAmount} onChange={(e) => setScAmount(e.target.value)} data-testid="admin-self-credit-amount" /></div>
            <ConfirmButton className="w-full" disabled={scBusy} beforeOpen={() => { if (!(parseFloat(scAmount) > 0)) { toast.error("Importo non valido"); return false; } return true; }} onConfirm={selfCredit} data-testid="admin-self-credit-submit" confirmTestId="admin-self-credit-confirm"
              title={`Confermi di aggiungere ${eur(parseFloat(scAmount) || 0)}?`} description={`Stai aggiungendo ${eur(parseFloat(scAmount) || 0)} al tuo wallet admin.`} confirmText={`Sì, aggiungi ${eur(parseFloat(scAmount) || 0)}`}>{scBusy ? "Aggiornamento…" : "Aggiungi credito"}</ConfirmButton>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
